import { useEffect, useId, useRef, useState } from "react";

import { Badge } from "@/design-system/primitives";
import type { QueuePriorityTag } from "./queuePriorityTags";

interface QueueTagBadgesProps {
  tags: QueuePriorityTag[];
  visibleLimit?: number;
}

/**
 * Keep queue rows compact without making later structured tags invisible.
 * The overflow disclosure is deliberately a real button so mouse, keyboard,
 * and touch users all have the same access to the complete tag set.
 */
export function QueueTagBadges({
  tags,
  visibleLimit = 3,
}: QueueTagBadgesProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  const visibleTags = tags.slice(0, visibleLimit);
  const hiddenCount = Math.max(0, tags.length - visibleTags.length);

  useEffect(() => {
    if (!open) return;

    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  if (tags.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-6" aria-label="Inbox tags">
      {visibleTags.map((tag) => (
        <Badge key={tag.key} tone={tag.tone} size="sm">
          {tag.label}
        </Badge>
      ))}
      {hiddenCount > 0 && (
        <div
          ref={rootRef}
          className="relative pointer-events-auto"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => {
            if (!rootRef.current?.contains(document.activeElement)) {
              setOpen(false);
            }
          }}
          onFocusCapture={() => setOpen(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node)) {
              setOpen(false);
            }
          }}
        >
          <button
            type="button"
            aria-expanded={open}
            aria-controls={tooltipId}
            aria-describedby={open ? tooltipId : undefined}
            aria-label={`Show all ${tags.length} inbox tags: ${tags
              .map((tag) => tag.label)
              .join(", ")}`}
            onClick={(event) => {
              event.stopPropagation();
              setOpen(true);
            }}
            className="h-20 rounded-pill bg-sunken px-10 font-mono text-10 uppercase leading-100 whitespace-nowrap text-ink-secondary hover:bg-line-subtle focus-visible:outline-none focus-visible:shadow-focus"
          >
            +{hiddenCount}
          </button>
          {open && (
            <div
              id={tooltipId}
              role="tooltip"
              className="absolute left-0 top-full z-40 mt-5 w-max max-w-[min(26rem,calc(100vw-2rem))] border border-line bg-raised p-10 shadow-lg"
            >
              <p className="mb-7 type-label-xs text-ink-muted">
                All inbox tags
              </p>
              <div className="flex flex-wrap gap-6">
                {tags.map((tag) => (
                  <Badge key={tag.key} tone={tag.tone} size="sm">
                    {tag.label}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
